/**
 * Gestione delle risposte 429 (rate limit WP-02 sulle route che accettano un PIN).
 * Il server risponde con `{ error: "<messaggio IT>", reason: "rate_limited" }` e `Retry-After`.
 */

export const RATE_LIMIT_FALLBACK_MESSAGE = "Troppi tentativi: riprova tra un minuto";
export const RATE_LIMIT_DEFAULT_RETRY_MS = 60_000;
const RATE_LIMIT_MIN_RETRY_MS = 1_000;
const RATE_LIMIT_MAX_RETRY_MS = 60_000;

/**
 * Converte `Retry-After` (secondi oppure data HTTP) in millisecondi.
 * Restituisce null se l'header manca o non è interpretabile.
 */
export function parseRetryAfterMs(
  header: string | null | undefined,
  now: number = Date.now()
): number | null {
  const t = header?.trim();
  if (!t) return null;
  if (/^\d+$/.test(t)) return Number(t) * 1000;
  const at = Date.parse(t);
  if (Number.isNaN(at)) return null;
  return Math.max(0, at - now);
}

/** Attesa da rispettare dopo un 429, limitata a [1s, 60s]; default 60s se l'header manca. */
export function readRetryAfterMs(res: Response, now: number = Date.now()): number {
  const ms = parseRetryAfterMs(res.headers.get("Retry-After"), now);
  if (ms == null) return RATE_LIMIT_DEFAULT_RETRY_MS;
  return Math.min(RATE_LIMIT_MAX_RETRY_MS, Math.max(RATE_LIMIT_MIN_RETRY_MS, ms));
}

/**
 * Per una risposta 429 restituisce il messaggio da mostrare all'utente
 * (il campo `error` del server se è una frase, altrimenti il fallback italiano).
 * Per qualsiasi altro status restituisce null.
 */
export async function readRateLimitError(res: Response): Promise<string | null> {
  if (res.status !== 429) return null;
  try {
    const data = (await res.clone().json()) as { error?: unknown } | null;
    const msg = typeof data?.error === "string" ? data.error.trim() : "";
    // Codici tecnici tipo "rate_limited" non vanno mostrati all'utente.
    if (msg && !/^[a-z_]+$/.test(msg)) return msg;
  } catch {
    /* corpo non JSON: usa il fallback */
  }
  return RATE_LIMIT_FALLBACK_MESSAGE;
}
